import {
  type ClientNotifications as ClientNotificationsResponse,
  useClientNotificationsQuery,
} from '@cartwheel/api/v1'
import {
  EmptyState,
  ExternalActionLink,
  FilterBar,
  LoadingView,
  Pagination,
  Select,
  Tooltip,
} from '@cartwheel/components'
import * as DataTable from '@cartwheel/components/data-table'
import { clsx } from 'clsx'
import { useMemo, useState } from 'react'

const ITEMS_PER_PAGE = 10
const ITERABLE_USER_PROFILE_URL = 'https://app.iterable.com/users/profiles'

const STATUS_CLASS: Record<string, string> = {
  sent: 'bg-sky-25 text-sea',
  opened: 'bg-sky-25 text-sea',
  clicked: 'bg-mint-25 text-forest-dark',
  bounced: 'bg-orange-25 text-orange',
  failed: 'bg-peach-50 text-brick',
  complaint: 'bg-peach-50 text-brick',
  delivered: 'bg-mint-25 text-forest-dark',
}

type Recipient = ClientNotificationsResponse['client']
type Notification = Recipient['notifications'][number]

function initials(name: string) {
  return name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

function iterableEventHistoryUrl(iterableUserId: string) {
  return `${ITERABLE_USER_PROFILE_URL}/${encodeURIComponent(iterableUserId)}/event/history`
}

function RecipientSummaryCard({ recipient }: { recipient: Recipient }) {
  return (
    <div className="mb-3 flex items-center gap-4 rounded-xl border border-sand-dark bg-white p-4">
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-mint-25 font-bold text-forest-dark">
        {initials(recipient.name)}
      </div>
      <div>
        <div className="text-[15px] font-bold text-charcoal">{recipient.name}</div>
        <ExternalActionLink
          href={iterableEventHistoryUrl(recipient.iterable_user_id)}
          label={recipient.iterable_user_id}
          className="font-mono text-xs"
        />
      </div>
    </div>
  )
}

function ChannelIcon({ eventType }: { eventType: string }) {
  const isEmail = eventType === 'emailSend'
  return (
    <div
      className={clsx(
        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
        isEmail ? 'bg-sky-25 text-sea' : 'bg-peach-25 text-brick-light'
      )}
    >
      {isEmail ? '✉' : '💬'}
    </div>
  )
}

function StatusPills({ statuses }: { statuses: Notification['statuses'] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {statuses.map((status) => (
        <Tooltip
          key={status.status_event_type}
          content={
            <ul className="list-disc pl-4">
              {status.status_ats.map((timestamp) => (
                <li key={timestamp}>{new Date(timestamp).toLocaleString()}</li>
              ))}
            </ul>
          }
        >
          <span
            className={clsx(
              'rounded-full px-2 py-0.5 text-[11px] font-bold',
              STATUS_CLASS[status.status] ?? 'bg-sand-50 text-charcoal-50'
            )}
          >
            {status.status}
          </span>
        </Tooltip>
      ))}
    </div>
  )
}

function NotificationRow({ notification }: { notification: Notification }) {
  return (
    <DataTable.Row>
      <DataTable.Cell label="Date">
        {new Date(notification.sent_at).toLocaleString()}
      </DataTable.Cell>
      <DataTable.Cell label="Notification">
        <div className="flex gap-3">
          <ChannelIcon eventType={notification.event_type} />
          <div>
            <div className="text-[13px] font-semibold text-charcoal">
              {notification.campaign_name}
            </div>
            <div className="mt-0.5 font-mono text-xs text-charcoal-50">
              {notification.event_type}
            </div>
            <span className="mt-1.5 inline-block rounded bg-mint-25 px-1.5 py-0.5 text-[10px] font-semibold text-forest-dark">
              Iterable
            </span>
          </div>
        </div>
      </DataTable.Cell>
      <DataTable.Cell label="Recipient">{notification.destination ?? '—'}</DataTable.Cell>
      <DataTable.Cell label="Event history">
        <StatusPills statuses={notification.statuses} />
      </DataTable.Cell>
    </DataTable.Row>
  )
}

function NotificationsTable({
  notifications,
  recipientName,
  page,
  onPageChange,
}: {
  notifications: Notification[]
  recipientName: string
  page: number
  onPageChange: (page: number) => void
}) {
  const totalPages = Math.ceil(notifications.length / ITEMS_PER_PAGE)
  const from = (page - 1) * ITEMS_PER_PAGE + 1
  const to = Math.min(page * ITEMS_PER_PAGE, notifications.length)
  const visibleNotifications = notifications.slice(from - 1, to)

  return (
    <div className="overflow-hidden rounded-xl border border-sand-dark bg-white">
      <DataTable.Root>
        <DataTable.Header>
          <DataTable.Row>
            <DataTable.HeaderCell>Date</DataTable.HeaderCell>
            <DataTable.HeaderCell>Notification</DataTable.HeaderCell>
            <DataTable.HeaderCell>Recipient</DataTable.HeaderCell>
            <DataTable.HeaderCell>Event history</DataTable.HeaderCell>
          </DataTable.Row>
        </DataTable.Header>
        <DataTable.Body>
          {visibleNotifications.map((notification) => (
            <NotificationRow key={notification.id} notification={notification} />
          ))}
        </DataTable.Body>
      </DataTable.Root>
      {notifications.length > ITEMS_PER_PAGE ? (
        <DataTable.Footer>
          <Pagination
            currentPage={page}
            itemsPerPage={ITEMS_PER_PAGE}
            totalPages={totalPages}
            totalCount={notifications.length}
            from={from}
            to={to}
            hasNextPage={page < totalPages}
            hasPrevPage={page > 1}
            onPageChange={onPageChange}
            onItemsPerPageChange={() => {}}
            showItemsPerPageSelector={false}
            ariaLabel={`${recipientName} notification pages`}
          />
        </DataTable.Footer>
      ) : null}
    </div>
  )
}

function RecipientNotificationsTable({
  recipient,
  recipientType,
}: {
  recipient: Recipient
  recipientType: 'Client' | 'Guardian'
}) {
  const [page, setPage] = useState(1)
  const [campaign, setCampaign] = useState<string | null>(null)
  const campaigns = useMemo(
    () => [
      { label: 'All campaigns', value: null },
      ...Array.from(
        new Set(recipient.notifications.map((notification) => notification.campaign_name))
      )
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ label: name, value: name })),
    ],
    [recipient.notifications]
  )
  const notifications = useMemo(
    () =>
      campaign
        ? recipient.notifications.filter((notification) => notification.campaign_name === campaign)
        : recipient.notifications,
    [recipient.notifications, campaign]
  )

  return (
    <section>
      <h3 className="mb-3 text-lg font-semibold text-charcoal">{recipientType} notifications</h3>
      <RecipientSummaryCard recipient={recipient} />
      <FilterBar
        rightSlot={
          <div className="w-72">
            <Select
              items={campaigns}
              value={campaign}
              onChange={(value) => {
                setCampaign(value)
                setPage(1)
              }}
            />
          </div>
        }
      >
        <span className="text-sm font-bold text-charcoal">
          Notifications timeline · {notifications.length} notifications
        </span>
      </FilterBar>
      {notifications.length === 0 ? (
        <EmptyState
          title={campaign ? `No notifications sent for ${campaign}` : 'No notifications sent'}
        />
      ) : (
        <NotificationsTable
          notifications={notifications}
          recipientName={recipient.name}
          page={page}
          onPageChange={setPage}
        />
      )}
    </section>
  )
}

export default function ClientNotifications({
  clientId,
  includeGuardian = false,
}: {
  clientId: string
  includeGuardian?: boolean
}) {
  const { data, isPending, error } = useClientNotificationsQuery({
    path: { client_id: clientId },
    query: { include_guardian: includeGuardian },
  })
  if (isPending) return <LoadingView title="Loading notification history..." />
  if (error || !data) return <div className="text-orange">Unable to load notification history.</div>
  return (
    <div className="space-y-8">
      <h2 className="text-xl font-semibold text-charcoal">Recent notification history</h2>
      <RecipientNotificationsTable recipient={data.client} recipientType="Client" />
      {includeGuardian &&
        (data.guardian ? (
          <RecipientNotificationsTable recipient={data.guardian} recipientType="Guardian" />
        ) : (
          <EmptyState title="No guardian on file" />
        ))}
    </div>
  )
}
